import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import {
  ArrowLeft, ChartLineUp, FloppyDisk, ArrowsClockwise, UsersThree,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const MONTHS_ES = ["Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"];
const MONTHS_FULL = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];

const fmtEUR = (n) => {
  const v = Number(n || 0);
  if (!v) return "0,00";
  return new Intl.NumberFormat("es-ES", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(v);
};
const fmtN = (n) => new Intl.NumberFormat("es-ES").format(Number(n || 0));
const fmtPct = (n) => `${new Intl.NumberFormat("es-ES", { maximumFractionDigits: 0 }).format(Number(n || 0))} %`;

const CONTRATO_LABEL = {
  empleado: "Empleado",
  autonomo: "Autónomo",
  empresa: "Empresa",
  socio: "Socio",
};

const ROW_DEFS = [
  { key: "expedientes", label: "Expedientes", fmt: fmtN, kind: "int" },
  { key: "honorarios", label: "Honorarios", fmt: fmtEUR, kind: "money" },
  { key: "km_gastos", label: "+ KM. + Gastos", fmt: fmtEUR, kind: "money", editable: true },
  { key: "fac", label: "FAC.", fmt: fmtEUR, kind: "money", emphasis: true, sep: true },
  { key: "fac_per_exp", label: "FAC / Expedientes", fmt: fmtEUR, kind: "money" },
  { key: "liquidacion", label: "Liquidación", fmt: fmtEUR, kind: "money", editable: true },
  { key: "pct_sobre_hono", label: "% Sobre Hono.", fmt: fmtPct, kind: "pct" },
  { key: "coste_empresa", label: "Coste Empresa", fmt: fmtEUR, kind: "money" },
  { key: "gastos_generales", label: "+ Gastos Generales", fmt: fmtEUR, kind: "money", editable: true },
  { key: "ct", label: "C.T.", fmt: fmtEUR, kind: "money", emphasis: true, sep: true },
  { key: "pct_fac_ct", label: "% FAC / C.T.", fmt: fmtPct, kind: "pct" },
  { key: "bb", label: "FAC – C.T. = B.B.", fmt: fmtEUR, kind: "money", emphasis: true },
  { key: "pct_bb", label: "% B.B.", fmt: fmtPct, kind: "pct" },
];

/* ====================== Aggregations ====================== */

function aggregateMonthly(monthly, activeMonths, tipo_contrato) {
  // Recalcula los totales / medias considerando solo los meses activos.
  const active = monthly.filter((m) => activeMonths.has(m.month));
  const sum = (k) => active.reduce((s, m) => s + (m[k] || 0), 0);
  const expedientes = sum("expedientes");
  const honorarios = sum("honorarios");
  const km = sum("km_gastos");
  const liquidacion = sum("liquidacion");
  const gastos_generales = sum("gastos_generales");
  const fac = honorarios + km;
  const coste_empresa = tipo_contrato === "empleado" ? liquidacion * 1.6 : liquidacion;
  const ct = coste_empresa + gastos_generales;
  const bb = fac - ct;
  return {
    expedientes,
    honorarios,
    km_gastos: km,
    fac,
    fac_per_exp: expedientes ? fac / expedientes : 0,
    liquidacion,
    pct_sobre_hono: honorarios ? (liquidacion / honorarios) * 100 : 0,
    coste_empresa,
    gastos_generales,
    ct,
    pct_fac_ct: ct ? (fac / ct) * 100 : 0,
    bb,
    pct_bb: fac ? (bb / fac) * 100 : 0,
  };
}

function buildTotalPeritos(peritos, activeMonths) {
  // Suma columna a columna de TODOS los peritos (después de aplicar sus respectivas fórmulas).
  const monthly = Array.from({ length: 12 }, (_, i) => {
    const m = i + 1;
    const r = {
      month: m, expedientes: 0, honorarios: 0, km_gastos: 0,
      fac: 0, fac_per_exp: 0, liquidacion: 0, pct_sobre_hono: 0,
      coste_empresa: 0, gastos_generales: 0, ct: 0, pct_fac_ct: 0,
      bb: 0, pct_bb: 0,
    };
    peritos.forEach((p) => {
      const row = p.monthly[i];
      r.expedientes += row.expedientes;
      r.honorarios += row.honorarios;
      r.km_gastos += row.km_gastos;
      r.liquidacion += row.liquidacion;
      r.gastos_generales += row.gastos_generales;
      r.coste_empresa += row.coste_empresa;
    });
    r.fac = r.honorarios + r.km_gastos;
    r.fac_per_exp = r.expedientes ? r.fac / r.expedientes : 0;
    r.pct_sobre_hono = r.honorarios ? (r.liquidacion / r.honorarios) * 100 : 0;
    r.ct = r.coste_empresa + r.gastos_generales;
    r.pct_fac_ct = r.ct ? (r.fac / r.ct) * 100 : 0;
    r.bb = r.fac - r.ct;
    r.pct_bb = r.fac ? (r.bb / r.fac) * 100 : 0;
    return r;
  });
  return {
    id: "__total__",
    code: "TOTAL",
    name: "Total peritos",
    role: "—",
    tipo_contrato: "—",
    monthly,
  };
}

/* ====================== Editable Cell ====================== */

function NumCell({ value, onSave, dataTestId }) {
  const [v, setV] = useState(value ?? 0);
  const orig = useRef(value);
  useEffect(() => { setV(value ?? 0); orig.current = value; }, [value]);
  const commit = () => {
    const num = parseFloat(String(v).replace(",", ".")) || 0;
    if (num !== orig.current) onSave(num);
  };
  return (
    <input
      type="number"
      step="0.01"
      value={v}
      onChange={(e) => setV(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
      data-testid={dataTestId}
      className="w-full text-right bg-white border border-[#E8EAED] rounded-md px-2 py-1 text-[12px] tabular-nums focus:border-brand focus:ring-2 focus:ring-brand/20 outline-none transition"
    />
  );
}

/* ====================== Page ====================== */

export default function RendimientoPeritos() {
  const navigate = useNavigate();
  const [year, setYear] = useState(new Date().getFullYear());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeIdx, setActiveIdx] = useState(0);   // 0..N-1 = perito, N = total
  const [activeMonths, setActiveMonths] = useState(new Set([1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]));
  const [includeSocios, setIncludeSocios] = useState(false);
  const [excludedSocios, setExcludedSocios] = useState(new Set());
  const [savingKey, setSavingKey] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/admin/rendimiento?year=${year}&include_socios=${includeSocios}`);
      setData(r.data);
      setActiveIdx(0);
    } catch (e) {
      console.error(e); toast.error("No se pudo cargar el rendimiento");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [year, includeSocios]);

  // Lista de peritos visibles (excluye socios marcados manualmente)
  const visiblePeritos = useMemo(() => {
    if (!data) return [];
    return data.peritos.filter((p) => !excludedSocios.has(p.id));
  }, [data, excludedSocios]);

  // Construye la pestaña "Total peritos"
  const totalTab = useMemo(() => {
    if (!data) return null;
    return buildTotalPeritos(visiblePeritos, activeMonths);
  }, [visiblePeritos, activeMonths, data]);

  const tabs = useMemo(() => [...visiblePeritos, totalTab].filter(Boolean), [visiblePeritos, totalTab]);
  const active = tabs[activeIdx];

  // Para totales/medias por columna
  const totales = useMemo(() => {
    if (!active) return null;
    return aggregateMonthly(active.monthly, activeMonths, active.tipo_contrato);
  }, [active, activeMonths]);

  const numActive = activeMonths.size || 1;
  const medias = useMemo(() => {
    if (!totales) return null;
    return {
      expedientes: totales.expedientes / numActive,
      honorarios: totales.honorarios / numActive,
      km_gastos: totales.km_gastos / numActive,
      fac: totales.fac / numActive,
      fac_per_exp: totales.fac_per_exp,    // ratio: ya está aplanado
      liquidacion: totales.liquidacion / numActive,
      pct_sobre_hono: totales.pct_sobre_hono, // pcts no se promedian, son ratio global
      coste_empresa: totales.coste_empresa / numActive,
      gastos_generales: totales.gastos_generales / numActive,
      ct: totales.ct / numActive,
      pct_fac_ct: totales.pct_fac_ct,
      bb: totales.bb / numActive,
      pct_bb: totales.pct_bb,
    };
  }, [totales, numActive]);

  const toggleMonth = (m) => {
    const n = new Set(activeMonths);
    n.has(m) ? n.delete(m) : n.add(m);
    setActiveMonths(n);
  };
  const toggleAllMonths = () => {
    if (activeMonths.size === 12) setActiveMonths(new Set());
    else setActiveMonths(new Set([1,2,3,4,5,6,7,8,9,10,11,12]));
  };

  const saveCell = async (peritoId, month, key, value) => {
    setSavingKey(`${peritoId}-${month}-${key}`);
    try {
      const { data: refreshed } = await api.put(
        `/admin/rendimiento/${peritoId}?year=${year}`,
        { month, [key]: value },
      );
      setData((prev) => {
        if (!prev) return prev;
        const peritos = prev.peritos.map((p) => p.id === peritoId ? refreshed : p);
        return { ...prev, peritos };
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo guardar");
    } finally { setSavingKey(null); }
  };

  const yearOptions = useMemo(() => {
    const cur = new Date().getFullYear();
    return [cur - 3, cur - 2, cur - 1, cur, cur + 1];
  }, []);

  return (
    <div data-testid="rendimiento-peritos-page">
      <header className="h-14 border-b border-[#E8EAED] bg-white px-6 flex items-center gap-3 sticky top-0 z-30">
        <button onClick={() => navigate(-1)} className="text-muted hover:text-foreground inline-flex items-center gap-1 text-xs">
          <ArrowLeft className="w-3.5 h-3.5" weight="regular" /> Atrás
        </button>
        <div className="text-muted">·</div>
        <div className="flex items-center gap-2 min-w-0">
          <span className="chip-icon chip-icon-amber"><ChartLineUp className="w-4 h-4" weight="regular" /></span>
          <h1 className="font-display font-semibold text-[15px] tracking-tight truncate">Rendimiento de peritos</h1>
        </div>
        <div className="ml-auto flex items-center gap-3">
          <label className="text-[11px] text-muted flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={includeSocios}
              onChange={(e) => setIncludeSocios(e.target.checked)}
              data-testid="rend-include-socios"
              className="!w-auto"
            />
            Incluir socios
          </label>
          <select
            value={year}
            onChange={(e) => setYear(parseInt(e.target.value, 10))}
            data-testid="rend-year"
            className="text-xs !w-auto"
          >
            {yearOptions.map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
          <button onClick={load} className="text-xs border border-[#E8EAED] rounded-md px-3 py-1.5 inline-flex items-center gap-1.5 bg-white hover:bg-[#FBFBFC] transition">
            <ArrowsClockwise className="w-3.5 h-3.5" weight="regular" /> Recargar
          </button>
        </div>
      </header>

      <div className="p-6 space-y-4 max-w-[1400px]">
        {/* Filtro de socios — chips toggleables */}
        {data && data.socios.length > 0 && (
          <div className="bg-white border border-[#E8EAED] rounded-[10px] px-4 py-3 flex flex-wrap items-center gap-2">
            <div className="text-[11px] uppercase tracking-wide text-muted flex items-center gap-1.5 font-semibold">
              <UsersThree className="w-3.5 h-3.5" weight="regular" /> Socios
            </div>
            {data.socios.map((s) => {
              const ex = excludedSocios.has(s.id);
              return (
                <button
                  key={s.id}
                  onClick={() => {
                    const n = new Set(excludedSocios);
                    ex ? n.delete(s.id) : n.add(s.id);
                    setExcludedSocios(n);
                  }}
                  data-testid={`rend-socio-${s.code}`}
                  className={`text-[11px] px-2.5 py-1 rounded-full border transition-colors ${
                    ex
                      ? "bg-white text-muted border-[#E8EAED] line-through"
                      : "bg-[#FDF0DB] text-[#B45309] border-[#F5D9A6]"
                  }`}
                >
                  {s.code}
                </button>
              );
            })}
            <span className="text-[11px] text-muted ml-2">(haz clic para excluir / incluir)</span>
          </div>
        )}

        {/* Tabs por perito */}
        <div className="table-card">
          <div className="border-b border-[#E8EAED] bg-[#FBFBFC] flex overflow-x-auto">
            {tabs.map((p, i) => {
              const isActive = i === activeIdx;
              const isTotal = p.id === "__total__";
              return (
                <button
                  key={p.id}
                  onClick={() => setActiveIdx(i)}
                  data-testid={`rend-tab-${isTotal ? "total" : p.code}`}
                  className={`px-5 py-2.5 text-[12px] font-semibold whitespace-nowrap transition-colors border-b-2 ${
                    isActive
                      ? "border-brand text-foreground bg-white"
                      : "border-transparent text-muted hover:text-foreground hover:bg-white"
                  } ${isTotal ? "ml-auto border-l border-l-[#E8EAED]" : ""}`}
                >
                  {isTotal ? "Total peritos" : p.code}
                </button>
              );
            })}
          </div>

          {!active || loading ? (
            <div className="p-10 text-center text-muted text-sm">Cargando…</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="table-modern" data-testid="rend-table">
                <thead>
                  <tr>
                    <th className="!text-left sticky left-0 z-10 !bg-[#FBFBFC] w-44">
                      <button onClick={toggleAllMonths} className="text-[11px] uppercase tracking-wide font-semibold text-muted hover:text-foreground transition">
                        {activeMonths.size === 12 ? "✓ Todos" : "Todos"}
                      </button>
                    </th>
                    {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                      <th key={m} className={`!text-center min-w-[92px] ${activeMonths.has(m) ? "" : "opacity-50"}`}>
                        <label className="flex flex-col items-center gap-1 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={activeMonths.has(m)}
                            onChange={() => toggleMonth(m)}
                            data-testid={`rend-month-${m}`}
                            className="!w-auto accent-brand"
                          />
                          <span className="text-[11px] uppercase tracking-wide font-semibold text-muted">{MONTHS_ES[m - 1]}</span>
                        </label>
                      </th>
                    ))}
                    <th className="!text-center min-w-[112px] !text-[#B45309] !bg-[#FDF0DB]">Totales</th>
                    <th className="!text-center min-w-[112px] !text-foreground !bg-[#F1F3F5]">Medias</th>
                  </tr>
                </thead>
                <tbody>
                  {ROW_DEFS.map((rd) => (
                    <tr key={rd.key} className={`${rd.emphasis ? "row-total" : ""} ${rd.sep ? "border-t-2 border-t-[#E8EAED]" : ""}`}>
                      <td className="!text-right sticky left-0 z-10 !bg-white whitespace-nowrap font-medium">
                        {rd.label}
                      </td>
                      {active.monthly.map((row) => {
                        const isEditable = rd.editable && active.id !== "__total__";
                        const val = row[rd.key];
                        const dim = !activeMonths.has(row.month);
                        return (
                          <td key={row.month} className={`cell-num ${dim ? "opacity-40" : ""}`}>
                            {isEditable ? (
                              <NumCell
                                value={val}
                                onSave={(num) => saveCell(active.id, row.month, rd.key, num)}
                                dataTestId={`rend-cell-${active.code}-${row.month}-${rd.key}`}
                              />
                            ) : (
                              <span data-testid={`rend-cell-${active.code}-${row.month}-${rd.key}`}>{rd.fmt(val)}</span>
                            )}
                          </td>
                        );
                      })}
                      <td className="cell-num !bg-[#FDF0DB] !text-[#B45309] font-semibold"
                          data-testid={`rend-total-${active.code}-${rd.key}`}>
                        {totales ? rd.fmt(totales[rd.key]) : "—"}
                      </td>
                      <td className="cell-num !bg-[#F1F3F5] !text-foreground"
                          data-testid={`rend-media-${active.code}-${rd.key}`}>
                        {medias ? rd.fmt(medias[rd.key]) : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Leyenda */}
        <div className="table-legend space-y-1.5">
          <div className="font-semibold text-[13px] mb-1">Leyenda y recordatorios</div>
          <div><strong>FAC.</strong> Facturación = Honorarios + KM/Gastos del perito</div>
          <div><strong>C.T.</strong> Coste Total = Coste Empresa + Gastos Generales</div>
          <div><strong>B.B.</strong> Beneficio Bruto = FAC. − C.T.</div>
          <div className="pt-1.5 text-muted">El <strong>Coste Empresa</strong> es la Liquidación si el perito es Autónomo o Empresa, o bien Liquidación × 1,6 cuando es Empleado. Tipo de contrato actual del perito activo: <strong>{active ? CONTRATO_LABEL[active.tipo_contrato] || active.tipo_contrato : "—"}</strong>.</div>
          <div className="pt-1.5 text-[11px] text-muted">{savingKey ? `Guardando ${savingKey}…` : "Edita los valores en Liquidación, +KM+Gastos y +Gastos Generales: se guardan al perder el foco."}</div>
        </div>
      </div>
    </div>
  );
}
